import React, { useRef, useState, useCallback, useEffect } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { ArrowLeft, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { portfolioData as fallbackData } from '../mock/portfolioData';

const API = process.env.REACT_APP_BACKEND_URL;

/* Normalize the API document (which stores [{id,url}, …]) into the shape
   the renderer expects: arrays of plain URL strings + heroIndex.
   Also returns a posMap (url → objectPosition) for per-image framing. */
function normalize(doc) {
  if (!doc) return null;
  const posMap = {};
  const urlsOf = (arr) =>
    (arr || []).map((x) => {
      if (typeof x === 'string') return x;
      if (x.objectPosition) posMap[x.url] = x.objectPosition;
      return x.url;
    });
  const shoots = (doc.shoots || []).map((s) => {
    const urls = urlsOf(s.images);
    const idx = (s.images || []).findIndex((img) => img.id === s.heroId);
    return {
      title: s.title,
      subtitle: s.subtitle,
      heroIndex: idx >= 0 ? idx : 0,
      images: urls,
    };
  });
  const sky = doc.skyHigh || { images: [] };
  const skyImgs = sky.images || [];
  const skyIdx = skyImgs.findIndex((img) => img.id === sky.heroId);
  return {
    mainShots: urlsOf(doc.mainShots),
    profiles: urlsOf(doc.profiles),
    shoots,
    statement: urlsOf(doc.statement),
    skyHigh: {
      heroIndex: skyIdx >= 0 ? skyIdx : 0,
      images: urlsOf(skyImgs),
    },
    movieFrames: urlsOf(doc.movieFrames),
    headings: doc.headings || {},
    posMap,
  };
}

/* Default heading values — used if API didn't return one */
const DEFAULT_HEADINGS = {
  profiles: { label: 'The Profiles', title: 'From every angle' },
  shoots: {
    label: 'The Shoots',
    title: 'Every frame, a story',
    desc: 'Curated collections — tap any image to view full screen.',
  },
  statement: { title: 'The Statement', subtitle: 'Elegant · Classy' },
  skyHigh: { title: 'Sky High', subtitle: 'Rooftop · Playful · Carefree' },
  movieFrames: {
    label: 'The Casting Board',
    title: 'Scene by Scene',
    desc: 'Every role, every frame — characters waiting to come alive.',
  },
};
const h = (data, section, key) =>
  (data.headings && data.headings[section] && data.headings[section][key]) ||
  DEFAULT_HEADINGS[section][key];

/* Append a width hint so the backend can serve a resized thumbnail.
   - /api/portfolio/image/...  → adds ?w=N to the existing endpoint
   - customer-assets.emergentagent.com URLs → routes through /api/portfolio/proxy
   - All other URLs → returned unchanged (no resize possible)
*/
const API_BASE = process.env.REACT_APP_BACKEND_URL;
const withWidth = (src, w) => {
  if (!src || !w) return src;
  if (src.includes('/api/portfolio/image/')) {
    return src + (src.includes('?') ? '&' : '?') + `w=${w}`;
  }
  if (src.includes('customer-assets.emergentagent.com')) {
    return `${API_BASE}/api/portfolio/proxy?w=${w}&url=${encodeURIComponent(src)}`;
  }
  return src;
};

/* ─── Fade-up on scroll ─── */
const Reveal = ({ children, className = '', delay = 0 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

/* ─── Lightbox ─── */
const Lightbox = ({ images, index, onClose, onPrev, onNext }) => {
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') onPrev();
      if (e.key === 'ArrowRight') onNext();
    };
    if (index !== null) window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [index, onClose, onPrev, onNext]);

  if (index === null) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center"
        onClick={onClose}
        data-testid="lightbox-overlay"
      >
        <button onClick={onClose} className="absolute top-5 right-5 z-10 text-white/60 hover:text-white transition-colors" data-testid="lightbox-close"><X size={28} /></button>
        <button onClick={(e) => { e.stopPropagation(); onPrev(); }} className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-10 text-white/40 hover:text-white transition-colors p-2" data-testid="lightbox-prev"><ChevronLeft size={36} /></button>
        <button onClick={(e) => { e.stopPropagation(); onNext(); }} className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-10 text-white/40 hover:text-white transition-colors p-2" data-testid="lightbox-next"><ChevronRight size={36} /></button>
        <motion.img key={index} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} src={withWidth(images[index], 1600)} alt="" decoding="async" className="max-h-[90vh] max-w-[92vw] object-contain" onClick={(e) => e.stopPropagation()} />
        <div className="absolute bottom-5 left-1/2 -translate-x-1/2 text-zinc-500 text-xs tracking-[0.2em]">{index + 1} / {images.length}</div>
      </motion.div>
    </AnimatePresence>
  );
};

/* ─── Clickable Image ─── */
const Img = ({ src, className = '', pos = 'top', onClick, eager = false, width }) => (
  <div className={`overflow-hidden cursor-pointer group ${className}`} onClick={onClick}>
    <img
      src={withWidth(src, width)}
      alt=""
      loading={eager ? 'eager' : 'lazy'}
      decoding="async"
      fetchPriority={eager ? 'high' : 'auto'}
      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
      style={{ objectPosition: pos }}
    />
  </div>
);

/* ─── Section Heading ─── */
const Heading = ({ label, title, desc }) => (
  <Reveal className="mb-3 md:mb-10">
    <div className="flex items-center gap-3 mb-2 flex-wrap">
      <div className="w-8 md:w-14 h-px bg-zinc-700" />
      <span className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-light">{label}</span>
      {desc && (
        <span className="md:hidden text-[10px] italic text-zinc-600 tracking-wide">
          · tap any photo to expand
        </span>
      )}
    </div>
    <h2 className="text-3xl sm:text-4xl md:text-5xl font-light tracking-tighter text-white" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{title}</h2>
    {desc && (
      <p className="hidden md:block mt-2 text-zinc-400 text-sm font-light tracking-wide max-w-lg">{desc}</p>
    )}
  </Reveal>
);

/* ─── Shoot Card ─── */
const ShootCard = ({ shoot, onImageClick }) => {
  const hero = shoot.images[shoot.heroIndex];
  const others = shoot.images.filter((_, i) => i !== shoot.heroIndex);

  return (
    <Reveal className="mb-12 md:mb-16">
      <div className="mb-3">
        <h3 className="text-xl sm:text-2xl md:text-3xl font-light tracking-tight text-white" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{shoot.title}</h3>
        <p className="mt-0.5 text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-light">{shoot.subtitle}</p>
      </div>

      {/* Desktop: hero stretches to match grid height */}
      <div className="hidden md:flex gap-1">
        <div className="w-[34%] flex-shrink-0 overflow-hidden rounded-sm">
          <Img src={hero} className="w-full h-full" pos="top" width={900} onClick={() => onImageClick(shoot.images, shoot.heroIndex)} />
        </div>
        <div className="flex-1 grid grid-cols-3 gap-1 auto-rows-min">
          {others.map((img, i) => (
            <Img key={img} src={img} className="w-full aspect-[3/4] rounded-sm" pos="top" width={400} onClick={() => onImageClick(shoot.images, shoot.images.indexOf(img))} />
          ))}
        </div>
      </div>

      {/* Mobile: compact 3-col */}
      <div className="md:hidden">
        <Img src={hero} className="w-full aspect-[3/4] rounded-sm mb-1" pos="top" width={700} onClick={() => onImageClick(shoot.images, shoot.heroIndex)} />
        <div className="grid grid-cols-3 gap-1">
          {others.map((img, i) => (
            <Img key={img} src={img} className="w-full aspect-[3/4] rounded-sm" pos="top" width={300} onClick={() => onImageClick(shoot.images, shoot.images.indexOf(img))} />
          ))}
        </div>
      </div>
    </Reveal>
  );
};

/* ─── Film Strip Sprocket ─── */
const SprocketStrip = () => (
  <div className="flex justify-between px-2 py-1.5">
    {Array.from({ length: 24 }).map((_, i) => (
      <div key={i} className="w-2.5 h-1.5 md:w-3 md:h-2 rounded-[1px] bg-zinc-800/80" />
    ))}
  </div>
);

/* ════════════════════════════════
   PORTFOLIO PAGE
   ════════════════════════════════ */
export default function CinematicPortfolio() {
  const [lightbox, setLightbox] = useState({ images: [], index: null });
  const [portfolioData, setPortfolioData] = useState({ ...fallbackData, posMap: {} });

  useEffect(() => {
    let alive = true;
    fetch(`${API}/api/portfolio/content`)
      .then((r) => (r.ok ? r.json() : null))
      .then((doc) => {
        const norm = normalize(doc);
        if (alive && norm) setPortfolioData(norm);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const openLightbox = useCallback((images, index) => {
    setLightbox({ images, index });
    document.body.style.overflow = 'hidden';
  }, []);

  const closeLightbox = useCallback(() => {
    setLightbox({ images: [], index: null });
    document.body.style.overflow = '';
  }, []);

  const prevImage = useCallback(() => {
    setLightbox(prev => ({ ...prev, index: prev.index > 0 ? prev.index - 1 : prev.images.length - 1 }));
  }, []);

  const nextImage = useCallback(() => {
    setLightbox(prev => ({ ...prev, index: prev.index < prev.images.length - 1 ? prev.index + 1 : 0 }));
  }, []);

  const skyHero = portfolioData.skyHigh.images[portfolioData.skyHigh.heroIndex];
  const skyOthers = portfolioData.skyHigh.images.filter((_, i) => i !== portfolioData.skyHigh.heroIndex);

  return (
    <div className="bg-[#0a0a0a] min-h-screen text-white">
      {/* Film grain */}
      <div className="fixed inset-0 pointer-events-none z-50 opacity-[0.02] mix-blend-overlay" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")` }} />

      {/* Nav */}
      <nav className="sticky top-0 z-40 bg-[#0a0a0a]/80 backdrop-blur-md border-b border-white/5">
        <div className="flex items-center justify-between px-4 md:px-10 py-3">
          <Link to="/" className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors group" data-testid="portfolio-back-link">
            <ArrowLeft size={14} className="group-hover:-translate-x-1 transition-transform" />
            <span className="text-[10px] uppercase tracking-[0.2em] font-light">Main Site</span>
          </Link>
          <span className="text-sm text-zinc-500 font-light tracking-wide hidden sm:block" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Komal Verma</span>
          <Link to="/#showreel" className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 hover:text-white transition-colors font-light" data-testid="portfolio-showreel-link">Watch Showreel</Link>
        </div>
      </nav>

      {/* ═══ MAIN CASTING SHOTS ═══ */}
      <section className="px-3 md:px-10 pt-3 md:pt-8" data-testid="portfolio-main-shots">
        <Reveal>
          <div className="grid grid-cols-3 gap-1 md:gap-2">
            {portfolioData.mainShots.map((src, i) => (
              <Img key={src} src={src} className="w-full aspect-[2/3] md:aspect-[3/4] rounded-sm" pos="top" width={500} eager={i < 3} onClick={() => openLightbox(portfolioData.mainShots, i)} />
            ))}
          </div>
        </Reveal>
      </section>

      {/* ═══ PROFILES ═══ */}
      <section className="px-3 md:px-10 pt-7 md:pt-20" data-testid="portfolio-profiles">
        <Reveal>
          <div className="text-center mb-3 md:mb-8">
            <div className="flex items-center justify-center gap-3 mb-1.5 md:mb-2">
              <div className="w-8 md:w-14 h-px bg-zinc-700" />
              <span className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-light">{h(portfolioData, 'profiles', 'label')}</span>
              <div className="w-8 md:w-14 h-px bg-zinc-700" />
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-light tracking-tight text-white italic" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{h(portfolioData, 'profiles', 'title')}</h2>
          </div>
        </Reveal>
        <Reveal>
          <div className="grid grid-cols-3 gap-1 md:gap-2">
            {portfolioData.profiles.map((src, i) => (
              <Img key={src} src={src} className="w-full aspect-[2/3] md:aspect-[3/4] rounded-sm" pos="top" width={400} onClick={() => openLightbox(portfolioData.profiles, i)} />
            ))}
          </div>
        </Reveal>
      </section>

      {/* ═══ SHOOT CARDS ═══ */}
      <section className="px-3 md:px-10 pt-10 md:pt-28 pb-4" data-testid="portfolio-shoots">
        <Heading label={h(portfolioData, 'shoots', 'label')} title={h(portfolioData, 'shoots', 'title')} desc={h(portfolioData, 'shoots', 'desc')} />
        {portfolioData.shoots.slice(0, 3).map((shoot) => (
          <ShootCard key={shoot.title} shoot={shoot} onImageClick={openLightbox} />
        ))}
      </section>

      {/* ═══ THE STATEMENT ═══ */}
      <section className="px-3 md:px-10 pb-4" data-testid="portfolio-statement">
        <Reveal className="mb-12 md:mb-16">
          <div className="mb-3">
            <h3 className="text-xl sm:text-2xl md:text-3xl font-light tracking-tight text-white" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{h(portfolioData, 'statement', 'title')}</h3>
            <p className="mt-0.5 text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-light">{h(portfolioData, 'statement', 'subtitle')}</p>
          </div>
          <div className="grid grid-cols-3 gap-1 md:gap-2">
            {portfolioData.statement.map((src, i) => (
              <Img key={src} src={src} className="w-full aspect-[3/4] rounded-sm" pos="top" width={500} onClick={() => openLightbox(portfolioData.statement, i)} />
            ))}
          </div>
        </Reveal>
      </section>

      {/* ═══ SHOOT CARDS (continued) ═══ */}
      <section className="px-3 md:px-10 pb-4" data-testid="portfolio-shoots-cont">
        {portfolioData.shoots.slice(3).map((shoot) => (
          <ShootCard key={shoot.title} shoot={shoot} onImageClick={openLightbox} />
        ))}
      </section>

      {/* ═══ SKY HIGH ═══ */}
      <section className="px-3 md:px-10 pb-8" data-testid="portfolio-skyhigh">
        <Reveal className="mb-12 md:mb-16">
          <div className="mb-3">
            <h3 className="text-xl sm:text-2xl md:text-3xl font-light tracking-tight text-white" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{h(portfolioData, 'skyHigh', 'title')}</h3>
            <p className="mt-0.5 text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-light">{h(portfolioData, 'skyHigh', 'subtitle')}</p>
          </div>
          <div className="hidden md:flex gap-1">
            <div className="w-[34%] flex-shrink-0 overflow-hidden rounded-sm">
              <Img src={skyHero} className="w-full h-full" pos="top" width={900} onClick={() => openLightbox(portfolioData.skyHigh.images, portfolioData.skyHigh.heroIndex)} />
            </div>
            <div className="flex-1 grid grid-cols-3 gap-1 auto-rows-min">
              {skyOthers.map((img, i) => (
                <Img key={img} src={img} className="w-full aspect-[3/4] rounded-sm" pos="top" width={400} onClick={() => openLightbox(portfolioData.skyHigh.images, portfolioData.skyHigh.images.indexOf(img))} />
              ))}
            </div>
          </div>
          <div className="md:hidden">
            <Img src={skyHero} className="w-full aspect-[3/4] rounded-sm mb-1" pos="top" width={700} onClick={() => openLightbox(portfolioData.skyHigh.images, portfolioData.skyHigh.heroIndex)} />
            <div className="grid grid-cols-3 gap-1">
              {skyOthers.map((img, i) => (
                <Img key={img} src={img} className="w-full aspect-[3/4] rounded-sm" pos="top" width={300} onClick={() => openLightbox(portfolioData.skyHigh.images, portfolioData.skyHigh.images.indexOf(img))} />
              ))}
            </div>
          </div>
        </Reveal>
      </section>

      {/* ═══ MOVIE FRAMES — Film Strip ═══ */}
      <section className="px-3 md:px-10 pt-8 pb-12" data-testid="portfolio-movie-frames">
        <Heading label={h(portfolioData, 'movieFrames', 'label')} title={h(portfolioData, 'movieFrames', 'title')} desc={h(portfolioData, 'movieFrames', 'desc')} />

        <Reveal>
          {/* Film strip container */}
          <div className="bg-zinc-950 rounded-sm border border-zinc-800/50 overflow-hidden">
            {/* Top sprocket holes */}
            <SprocketStrip />

            {/* Film frames grid */}
            <div className="px-1.5 md:px-2">
              <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-5 gap-[3px] md:gap-1">
                {portfolioData.movieFrames.map((src, i) => (
                  <div
                    key={src}
                    className="relative cursor-pointer group"
                    onClick={() => openLightbox(portfolioData.movieFrames, i)}
                  >
                    {/* Film frame */}
                    <div className="bg-zinc-900 p-[2px] md:p-[3px] rounded-[1px]">
                      <div className="relative overflow-hidden">
                        {/* Letterbox bars */}
                        <div className="absolute top-0 left-0 right-0 h-[4px] md:h-[6px] bg-black z-10" />
                        <div className="absolute bottom-0 left-0 right-0 h-[4px] md:h-[6px] bg-black z-10" />
                        <img
                          src={withWidth(src, 350)}
                          alt=""
                          loading="lazy"
                          decoding="async"
                          className="w-full aspect-[3/4] object-cover transition-all duration-500 group-hover:scale-110 group-hover:brightness-110"
                          style={{ objectPosition: portfolioData.posMap?.[src] || 'top' }}
                        />
                        {/* Hover glow */}
                        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 shadow-[inset_0_0_20px_rgba(78,205,196,0.15)]" />
                      </div>
                    </div>
                    {/* Frame number */}
                    <div className="absolute bottom-[3px] right-[4px] md:bottom-1 md:right-1.5 text-[7px] md:text-[9px] text-zinc-600/60 font-mono z-20">
                      {String(i + 1).padStart(2, '0')}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Bottom sprocket holes */}
            <SprocketStrip />
          </div>

          {/* Film strip label */}
          <div className="flex items-center justify-between mt-3 px-1">
            <span className="text-[9px] text-zinc-600 font-mono tracking-wider">KODAK 5219 500T</span>
            <span className="text-[9px] text-zinc-600 font-mono tracking-wider">20 FRAMES</span>
            <span className="text-[9px] text-zinc-600 font-mono tracking-wider">KV-PORTFOLIO</span>
          </div>
        </Reveal>
      </section>

      {/* ═══ CLOSING CTA ═══ */}
      <section className="px-6 md:px-12 py-16 md:py-24 text-center" data-testid="portfolio-cta">
        <Reveal>
          <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-light mb-4">Let's create something extraordinary</p>
          <h2 className="text-2xl sm:text-3xl md:text-5xl font-light tracking-tighter text-white mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Ready for the next story?</h2>
          <Link to="/#contact" className="inline-block px-8 py-3 border border-white/20 text-xs uppercase tracking-[0.15em] text-white hover:bg-white hover:text-black transition-all duration-500 font-light" data-testid="portfolio-cta-email">Get in Touch</Link>
        </Reveal>
      </section>

      <footer className="border-t border-white/5 px-6 md:px-12 py-5 flex items-center justify-between">
        <span className="text-sm text-zinc-600 font-light" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Komal Verma</span>
        <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-700">Portfolio</span>
      </footer>

      <Lightbox images={lightbox.images} index={lightbox.index} onClose={closeLightbox} onPrev={prevImage} onNext={nextImage} />
    </div>
  );
}
